import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { Button, Checkbox, Col, Form, Input, message, Modal, Row } from 'antd'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { useMutation } from '@apollo/client'
import { uuid } from 'uuidv4'
import { CREATE_SIGNATURE } from './actions'
import { nameRule } from './Rules'
import { useGlobalQuery } from 'hooks/useGlobalQuery'
import { termsStyleCss } from 'components/Common'

interface FormValues {
  first_name: string
  last_name: string
  agreed: boolean
}

const { useForm } = Form

const TermsAndConditions = ({
  isAdmin,
  merchantId,
  registeredCompanyName = '',
  merchantName
}: {
  isAdmin: boolean
  merchantId: string
  registeredCompanyName: string | undefined
  merchantName: string
}) => {
  const [form] = useForm()
  const [enableSubmit, setEnableSubmit] = useState({
    agreed: false,
    authorised: false
  })
  const { signatures, isLoading, terms, refetchSignatures } = useGlobalQuery()

  const [showTerms, setShowTerms] = useState<boolean>(false)

  const termsAndConditions = (terms ?? [])[0]?.terms_and_conditions
  const updatedAt = dayjs((terms ?? [])[0]?.updated_at).format('D MMM YYYY')
  const latestTermsId = (terms ?? [])[0]?.id
  const latestSignature = (signatures ?? [])[0]?.terms_id

  const [createSignature, { loading: createSignatureLoading }] = useMutation(
    CREATE_SIGNATURE,
    {
      fetchPolicy: 'no-cache'
    }
  )

  useEffect(() => {
    if (isLoading) return
    if (sessionStorage.getItem(`terms-merchant-id-${merchantId}`) === 'skip')
      return setShowTerms(false)

    // checks if admin has signed the latest terms and conditions
    if (latestTermsId !== latestSignature) {
      setShowTerms(true)

      // if signature is outdated, makes sure to remove skip in sessionStorage
      sessionStorage.removeItem(`terms-merchant-id-${merchantId}`)
    } else {
      setShowTerms(false)
    }
  }, [latestTermsId, latestSignature, sessionStorage, isLoading])

  const onFinish = async (values: FormValues) => {
    const { first_name, last_name, agreed } = values

    const variables = {
      firstName: first_name,
      lastName: last_name,
      termsId: latestTermsId,
      merchantId: merchantId,
      agreed: agreed,
      id: uuid()
    }

    form
      .validateFields()
      .then(() =>
        createSignature({
          variables
        })
      )
      .then(() => {
        if (refetchSignatures) refetchSignatures()
        message.success('Successfully signed T&Cs!', 5)
      })
      .catch((error) =>
        message.error(`Unable to sign T&Cs due to ${error.message}`, 5)
      )
  }

  return (
    (isAdmin && (
      <>
        <TermsModal
          closable={false}
          open={showTerms}
          centered
          title={
            <Row className='_mb-0' justify='space-between' align='middle'>
              <Col className='title'>Terms & Conditions</Col>
              <Col>
                <FullWidthButton
                  data-testid='terms-and-conditions-skip'
                  type='ghost'
                  loading={createSignatureLoading}
                  className='-secondary terms-skip'
                  onClick={() => {
                    sessionStorage.setItem(
                      `terms-merchant-id-${merchantId}`,
                      'skip'
                    )
                    setShowTerms(false)
                  }}
                >
                  Skip
                </FullWidthButton>
              </Col>
              <Col className='last-updated'>
                <span>Last Updated:</span> <strong>{updatedAt}</strong>
              </Col>
            </Row>
          }
          style={{
            maxWidth: '672px'
          }}
          width='100%'
          footer={[
            <Form
              form={form}
              layout='vertical'
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 24 }}
              onFinish={onFinish}
              className='custom-content'
            >
              <Form.Item name='agreed' valuePropName='checked'>
                <FullWidthCheckbox
                  data-testid='agreed-checkbox'
                  onChange={(e) => {
                    const agreed = e.target.checked
                    setEnableSubmit((prev) => ({ ...prev, agreed }))
                  }}
                >
                  By clicking 'I agree', I acknowledge I have read and agree to
                  be bound by the Terms and Conditions set out above.
                </FullWidthCheckbox>
              </Form.Item>
              <Form.Item name='authorised' valuePropName='checked'>
                <FullWidthCheckbox
                  data-testid='authorised-checkbox'
                  onChange={(e) => {
                    const authorised = e.target.checked
                    setEnableSubmit((prev) => ({ ...prev, authorised }))
                  }}
                >
                  I am authorised on behalf of{' '}
                  <strong>{registeredCompanyName || merchantName}</strong> to
                  enter into these Terms and Conditions
                </FullWidthCheckbox>
              </Form.Item>
              <Row gutter={8} justify='space-between' className='_mb-0'>
                <Col span='10'>
                  <Form.Item
                    name='first_name'
                    className='_mb-0'
                    rules={nameRule}
                  >
                    <Input type='text' placeholder='First name' required />
                  </Form.Item>
                </Col>
                <Col span='10'>
                  <Form.Item
                    name='last_name'
                    className='_mb-0'
                    rules={nameRule}
                  >
                    <Input type='text' placeholder='Last name' required />
                  </Form.Item>
                </Col>
                <Col span='4'>
                  <Form.Item className='_mb-0 _mt-2' shouldUpdate>
                    {() => {
                      return (
                        <FullWidthButton
                          data-testid='terms-and-conditions-submit'
                          type='default'
                          htmlType='submit'
                          disabled={
                            !enableSubmit.agreed || !enableSubmit.authorised
                          }
                        >
                          I agree
                        </FullWidthButton>
                      )
                    }}
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          ]}
        >
          {termsAndConditions && (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(termsAndConditions, {
                  USE_PROFILES: { html: true },
                  ADD_ATTR: ['target']
                })
              }}
            />
          )}
        </TermsModal>
      </>
    )) || <></>
  )
}

const FullWidthButton = styled(Button)`
  width: 100%;
`

const FullWidthCheckbox = styled(Checkbox)`
  width: 100%;
`

const TermsModal = styled(Modal)`
  font-size: 14px;

  .ant-modal-title .ant-col.title {
    color: #4a4a68;
    font-size: 18px;
  }

  .terms-skip {
    padding: 3.2px 16px;
    max-height: 28px;
    opacity: 0;
    cursor: default;
  }

  .ant-modal-title .ant-col.last-updated {
    display: flex;
    align-items: center;
    margin-top: 4px;

    color: #77778e;
    font-size: 10px;
  }

  .ant-modal-title .ant-col.last-updated span {
    display: inline-block;
    line-height: 1;

    text-transform: uppercase;
  }

  .ant-modal-title .ant-col.last-updated > strong {
    margin-left: 8px;
    line-height: 1;

    color: #4a4a68;
    font-size: 14px;
  }

  .ant-modal-content {
    display: flex;
    flex-flow: column;

    max-height: 704px;
  }

  .ant-modal-body > div {
    overflow-y: scroll;
    max-height: 452px;

    font-size: 14px;
    line-height: 140%;
  }

  .ant-modal-footer {
    background-color: #fafafb;
  }

  .ant-form label {
    font-size: 14px;
    line-height: 140%;
  }

  .ant-modal-footer span {
    text-align: left;
  }

  .custom-content .ant-form-item-explain-error {
    margin: 6px 0 0 0;
  }

  ${termsStyleCss}
`

export default TermsAndConditions
